import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEventById } from '../api/events.api';
import { Event } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const EventDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await getEventById(id);
        setEvent(response.data);
      } catch (err) {
        // Silent fail for prototype.
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!event) {
    return (
      <div className="container py-5">
        <div className="alert alert-secondary">Event details will be updated soon.</div>
        <Link to="/events" className="btn btn-outline-secondary">
          Back to Events
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/events" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Events
        </Link>
      </div>
      <div className="card card-surface mb-4">
        {event.bannerUrl && (
          <img src={event.bannerUrl} className="card-img-top" alt={event.name} />
        )}
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
            <div className="flex-grow-1">
              <h1 className="h3 mb-2" style={{ fontSize: 'clamp(1.25rem, 3vw, 1.75rem)' }}>{event.name}</h1>
              <p className="text-muted-soft mb-3" style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}>{event.description || 'High intensity, elite competition.'}</p>
              <div className="d-flex flex-wrap gap-2 gap-md-3">
                <span className="badge bg-info text-white badge-status">
                  {event.status || 'Open'}
                </span>
                <span className="text-muted-soft">
                  {event.startDate ? new Date(event.startDate).toLocaleDateString() : 'TBA'}
                  {event.endDate ? ` - ${new Date(event.endDate).toLocaleDateString()}` : ''}
                </span>
                <span className="text-muted-soft">
                  <i className="bi bi-geo-alt me-1" />
                  {event.location || 'Location TBA'}
                </span>
              </div>
            </div>
            <div className="d-flex flex-column align-items-start align-items-lg-end gap-3 flex-shrink-0">
              <span className="badge bg-success badge-status">
                {event.registrationOpen ? 'Registration Open' : 'Registration Closed'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-8">
          <div className="card card-surface h-100">
            <div className="card-body">
              <h4 className="mb-3">Event Overview</h4>
              <p className="text-muted-soft">
                {event.description ||
                  'Prepare your roster, confirm your availability, and get ready for matchday operations. This event features certified referees and live score tracking.'}
              </p>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card card-surface h-100">
            <div className="card-body">
              <h5 className="mb-3">Registration Status</h5>
              <p className="text-muted-soft">
                {event.registrationOpen
                  ? 'Registration is open. Players can register through the player registration page.'
                  : 'Registration is currently closed. Check back for updates.'}
              </p>
              <Link to="/register" className="btn btn-outline-secondary w-100">
                Register as Player
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsPage;
